const localeInfo = {};

interface LocaleInfo {
    decimalSeparator;
    thousandsSeparator;
    floatingPointRegex;
}

function testLocale(locale: string) {
    const testNumber = 1000.9;
    const testText = testNumber.toLocaleString(locale);
    const pattern = `1(.+)000(.+)9`;

    const results = new RegExp(pattern).exec(testText);

    // eslint-disable-next-line
    const thousandsSeparator = (results && results[1]) || ",";
    // eslint-disable-next-line
    const decimalSeparator = (results && results[2]) || ".";

    const floatingPointRegex = floatingPointCharacterRegex(decimalSeparator);

    localeInfo[locale] = {
        decimalSeparator,
        thousandsSeparator,
        floatingPointRegex
    };
}

export function getLocaleSeparators(locale: string): LocaleInfo {
    if (!localeInfo[locale]) {
        testLocale(locale);
    }
    return localeInfo[locale];
}

/**
 * A regex that matches a string of length 1 (i.e. a standalone character)
 * if and only if it is a floating-point number character as defined by W3C:
 * https://www.w3.org/TR/2012/WD-html-markup-20120329/datatypes.html#common.data.float
 */
function floatingPointCharacterRegex(decimalSeparator: string) {
    decimalSeparator = decimalSeparator.replace(".", "\\.");
    const localeFloatingPointNumericCharacterRegex = new RegExp("^[0-9\\+\\-" + decimalSeparator + "]$");
    return localeFloatingPointNumericCharacterRegex;
}

/**
 * Checks if the keyboard event represents a single character
 * @param e the event
 */
export function isSingleCharacterEvent(e: KeyboardEvent) {
    // unit tests may not include e.key. don't bother disabling those events.
    if (e.key == null) {
        return true;
    }

    // allow modified key strokes that may involve letters and other
    // non-numeric/invalid characters (Cmd + A, Cmd + C, Cmd + V, Cmd + X).
    if (e.ctrlKey || e.altKey || e.metaKey) {
        return true;
    }

    // keys that print a single character when pressed have a `key` name of
    // length 1. every other key has a longer `key` name (e.g. "Backspace",
    // "ArrowUp", "Shift"). since none of those keys can print a character
    // to the field--and since they may have important native behaviors
    // beyond printing a character--we don't want to disable their effects.
    return e.key.length === 1;
}

/**
 * Rounds a number according to the amount of decimal places
 * @param value the number
 * @param maxPrecision the maximum amount of decimal places
 */
export function toMaxPrecision(value: number, maxPrecision: number) {
    const scaleFactor = Math.pow(10, maxPrecision);
    return Math.round(value * scaleFactor) / scaleFactor;
}

export function parseStringToStringNumber(value: string, locale?: string) {
    const { decimalSeparator } = getLocaleSeparators(locale);
    const sanitizedString = sanitizeNumericInput(value, locale);
    return sanitizedString.replace(decimalSeparator, ".");
}

export function sanitizeNumericInput(value: string, locale?: string) {
    const { floatingPointRegex: regex } = getLocaleSeparators(locale);

    return value
        .split("")
        .filter((character) => regex.test(character))
        .join("");
}

export function clamp(value: number, min?: number, max?: number) {
    value = min != null && value < min ? min : value;
    return max != null && value > max ? max : value;
}

// eslint-disable-next-line
export function isValidNumber(value: any) {
    return typeof value === "number" && !Number.isNaN(value);
}

export function range(size: number, startAt: number = 1): Array<number> {
    return [...Array(size).keys()].map((i) => i + startAt);
}
